import React, { useState } from "react";
import {
    ActivateFeatureButton,
    Button,
    DataTable,
    DataTableEditActionButton,
    DataTableViewActionButton,
    DataTableCell,
    DataTableContainer,
    DataTableHeaderCell,
    FlexContainer,
    LottieLoadingIndicator,
    PortalSearchInput,
    PortalTableActions,
    PortalTableControls,
    UpperControls
} from "src/components";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import styled from "styled-components";
import LinkClicksModal from "./LinkClicksModal";
import { TableRowCell } from "src/components/TableRowCell";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";

const TableButton = styled(Button)`
    border-radius: 8px !important;
    border: ${p => p.border || "none"};
    height: 42px;
    width: ${p => p.width};
    color: #fff;
    background-color: ${p => p.backgroundColor};
    box-sizing: border-box;
    font-size: 0.75rem;
    font-weight: 700;
    margin-left: ${p => p.marginLeft};
    margin-right: ${p => p.marginRight};
    padding: 0 !important;

    @media (max-width: 768px) {
        margin-right: 0rem;
    }
`;

const ImgContainer = styled.div`
    width: 83px;
    height: 47px;
    display: flex;
    justify-content: center;
    border: 1px solid #D8DEEC;
    border-radius: 8px;
`;

const Img = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    align-self: center;
`;

const OrganisationsTable = ({
    user,
    donationList,
    isFetching,
    allDonationsChecked,
    handleCheckAllDonations,
    checkedDonations,
    handleCheckDonation,
    handleEditDonation,
    handleCreateDonation,
    handleShowDeleteDialog
}) => {

    const [showLinkClicksModal, setShowLinkClicksModal] = useState(false);
    const [donationData, setDonationData] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    const handleSearchChange = e => {
        setSearchQuery(e.target.value);
    };

    const filteredDonations = donationList.filter(donation => {
        const { organisationName, headline, description } = donation;
        const searchTerms = `${organisationName.toLowerCase()} ${headline.toLowerCase()} ${description.toLowerCase()}`;
        
        if (searchQuery.length > 0) {
            const isMatchingSearch = searchTerms.includes(searchQuery.toLowerCase());
            return isMatchingSearch;
        }

        return true;
    });


    const handleCloseLinkClicksModal = () => {
        setDonationData(null);
        setShowLinkClicksModal(false);
    };

    const handleShowLinkClicksModal = donation => {
        setDonationData(donation);
        setShowLinkClicksModal(true);
    };

    const userGroupText = donation => {
        if (donation.displayToEmployees && donation.displayToClients) {
            return `${capitalizeString(pluralize(customNames.employees))} & ${capitalizeString(pluralize(customNames.clients))}`;
        } else if (donation.displayToEmployees) {
            return `${capitalizeString(pluralize(customNames.employees))}`;
        } else if (donation.displayToClients) {
            return `${capitalizeString(pluralize(customNames.clients))}`;
        }
    };

    const renderExtraRows = () => {
        const minRows = 10;
        const noOfRowsToRender = minRows - donationList.length;
        const extraRows = [];

        for (let i = 0; i < noOfRowsToRender; i++) {
            extraRows.push(
                <tr key={`empty-${i}`} className="placeholder-row">
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                    <DataTableCell $empty>&nbsp;</DataTableCell>
                </tr>
            );
        }

        return extraRows;
    };

    return (
        <>
            { isFetching && (
                <FlexContainer
                    justifyContent="center"
                    alignItems="center"
                    color="#fff"
                    style={{
                        maxWidth: "94.5rem",
                        height: "44.188rem",
                        border: "1px solid #D8DEEC",
                        borderRadius: "16px",
                        background: "#FFFFFF",
                        boxShadow: "0 18px 36px rgba(29, 42, 89, 0.08)"
                    }}
                >
                    <LottieLoadingIndicator />
                </FlexContainer>
            )}

            { !isFetching && (
                <FlexContainer style={{ maxWidth: "94.5rem" }}>
                    <UpperControls>
                        <PortalTableControls>
                            <PortalSearchInput placeholder="Search donation" type="text" value={searchQuery} onChange={handleSearchChange} />
                            {checkedDonations.length > 0 && (
                                <TableButton
                                    backgroundColor="#ff7171"
                                    width="10.75rem"
                                    onClick={() => handleShowDeleteDialog()}
                                >
                                    Delete Donations
                                </TableButton>
                            )}
                        </PortalTableControls>
                        <PortalTableActions>
                            <ActivateFeatureButton
                                width="13.125rem"
                                onClick={handleCreateDonation}
                            >
                                Create Donation
                            </ActivateFeatureButton>
                        </PortalTableActions>
                    </UpperControls>

                    <DataTableContainer maxWidth="94.5rem">
                        <DataTable>
                            <thead>
                                <tr>
                                    <DataTableHeaderCell width="4%" textAlign="center">
                                        <label>
                                            <Checkbox
                                                key="selectAll"
                                                readOnly={false}
                                                checked={allDonationsChecked}
                                                onChange={handleCheckAllDonations}
                                            />
                                        </label>
                                    </DataTableHeaderCell>
                                    <DataTableHeaderCell width="9%">Image</DataTableHeaderCell>
                                    <DataTableHeaderCell width="16%">Organisation Name</DataTableHeaderCell>
                                    <DataTableHeaderCell width="25%">Heading</DataTableHeaderCell>
                                    <DataTableHeaderCell width="10%">User Groups</DataTableHeaderCell>
                                    <DataTableHeaderCell width="9%" textAlign="center">Insights</DataTableHeaderCell>
                                    <DataTableHeaderCell width="9%" textAlign="center">Edit Donation</DataTableHeaderCell>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDonations.map(donation => (
                                    <tr key={donation.uuid}>
                                        <DataTableCell textAlign="center" whiteSpace="normal">
                                            <label>
                                                <Checkbox
                                                    readOnly={false}
                                                    checked={checkedDonations.includes(donation.uuid)}
                                                    onChange={(e) => handleCheckDonation(e, donation.uuid)}
                                                />
                                            </label>
                                        </DataTableCell>
                                        <DataTableCell whiteSpace="normal">
                                            <ImgContainer>
                                                <Img src={donation.image.imageUrl} />
                                            </ImgContainer>
                                        </DataTableCell>
                                        <DataTableCell whiteSpace="normal">{donation.organisationName}</DataTableCell>
                                        <DataTableCell whiteSpace="normal"><TableRowCell content={donation.headline} clamp={1}/></DataTableCell>
                                        <DataTableCell whiteSpace="normal">{userGroupText(donation) }</DataTableCell>
                                        <DataTableCell textAlign="center" whiteSpace="normal">
                                            <DataTableViewActionButton
                                                onClick={() => handleShowLinkClicksModal(donation) }
                                            >
                                                View
                                            </DataTableViewActionButton>
                                        </DataTableCell>
                                        <DataTableCell textAlign="center" whiteSpace="normal">
                                            <DataTableEditActionButton
                                                onClick={() => handleEditDonation(donation) }
                                            >
                                                Edit
                                            </DataTableEditActionButton>
                                        </DataTableCell>
                                    </tr>
                                ))}

                                {renderExtraRows()}
                            </tbody>
                        </DataTable>
                    </DataTableContainer>
                </FlexContainer>
            )}

            { showLinkClicksModal && 
                <LinkClicksModal
                    user={user}
                    donationData={donationData}
                    showLinkClicksModal={showLinkClicksModal}
                    handleCloseLinkClicksModal={handleCloseLinkClicksModal}
                />
            }
        </>
    );
};

export default OrganisationsTable;
